import React from 'react';
import { Phone, Car, DollarSign, CheckCircle, UserCheck, MapPin } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { capitalizeCityName } from '../routes/seoRoutes';

export const HowItWorks: React.FC = () => {
  // Use optional chaining to handle cases where context might not be available
  const { city } = useAppContext() || {};
  const capitalizedCity = city ? capitalizeCityName(city) : 'Reggio Emilia';

  const scrollToForm = () => {
    const formSection = document.getElementById('quote-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const steps = [
    {
      icon: <Phone className="w-8 h-8 " />,
      title: '1. Compila il Form',
      description: `Compila il form online. Ti risponderemo entro 30 minuti.`,
      details: ['Chiamata gratuita', 'Valutazione preliminare', 'Appuntamento fissato']
    },
    {
      icon: <Car className="w-8 h-8 " />,
      title: '2. Valutazione',
      description: `Il nostro esperto valuterà la tua auto a ${capitalizedCity} o a domicilio.`,
      details: ['Ispezione completa', 'Valutazione di mercato', 'Offerta competitiva']
    },
    {
      icon: <DollarSign className="w-8 h-8 " />,
      title: '3. Pagamento',
      description: 'Se accetti l\'offerta, ricevi il pagamento immediato e firmiamo i documenti.',
      details: ['Pagamento immediato', 'Documenti gestiti', 'Ritiro veicolo']
    }
  ];

  return (
    <section id="come-funziona" className="py-20 bg-white">
      <div className="container px-4 mx-auto">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Come Funziona
          </h2>
          <p className="max-w-3xl mx-auto text-xl text-gray-600">
            Il processo per vendere la tua auto è semplice e veloce. Ecco come funziona:
          </p>
        </div>

        {/* Client Satisfaction Section */}
        <div className="grid grid-cols-1 gap-12 mb-20 lg:grid-cols-2">
          {/* Left Column - Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
              <img
                src="/img/abour-sect.jpg"
                alt="Clienti Soddisfatti"
                className="object-cover w-full h-full"
              />
              {/* Overlay Stats */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white bg-gradient-to-t from-black/80">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="flex items-center text-3xl font-bold">
                      <UserCheck className='mr-2 w-7 h-7'/>
                      10k+
                    </h4>
                    <p className="text-sm">Clienti Soddisfatti</p>
                  </div>
                  <div>
                    <h4 className="flex items-center text-3xl font-bold">
                      <MapPin className='mr-2 w-7 h-7'/>
                      7
                    </h4>
                    <p className="text-sm">Regioni Coperte</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="space-y-8">
            <div className='flex flex-col gap-4'>
            <span className="text-[#fdc751] font-semibold text-lg mb-2 block">I NOSTRI NUMERI</span>
              <h3 className="mb-4 text-3xl font-bold">
                {city ? `La soluzione più rapida per vendere la tua auto a ${capitalizedCity}` : 'La soluzione più rapida per vendere la tua auto'}
              </h3>
              <p className="mb-6 text-xl text-gray-600">
                {city 
                  ? `Vendere un'auto online a ${capitalizedCity} non è mai stato così semplice! Con oltre 10.000 clienti soddisfatti, il nostro obiettivo è quello di facilitare la vendita e dimezzare i tempi.`
                  : 'Vendere un\'auto online non è mai stato così semplice! Con oltre 10.000 clienti soddisfatti, il nostro obiettivo è quello di facilitare la vendita e dimezzare i tempi.'
                }
              </p>
              <p className="text-gray-600">
                <strong>Operiamo principalmente nel Nord Italia:</strong>
              </p>
              <ul className="ml-4 space-y-1 text-gray-600 list-disc list-inside">
                <li>Emilia-Romagna</li>
                <li>Lombardia</li>
                <li>Piemonte</li>
                <li>Veneto</li>
                <li>Trentino-Alto Adige</li>
                <li>Friuli-Venezia Giulia</li>
                <li>Valle d'Aosta</li>
              </ul>
              <p className="font-medium text-gray-600">
                Fai parte anche tu dei nostri clienti soddisfatti!
              </p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="hidden gap-8 mb-16 md:grid md:grid-cols-3">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Step Number */}
              <div className="absolute z-10 flex items-center justify-center w-12 h-12 text-xl font-bold text-white bg-[#fdc751] rounded-full -top-4 -left-4">
                {index + 1}
              </div>

              {/* Step Content */}
              <div className="h-full p-8 bg-gray-50 rounded-xl">
                <div className="mb-6 text-blue-600">
                  {step.icon}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-gray-900">
                  {step.title}
                </h3>
                <p className="mb-6 text-gray-600">
                  {step.description}
                </p>
                <ul className="space-y-3">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="flex-shrink-0 w-4 h-4 mr-3 text-green-500" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-600 transform -translate-y-1/2 z-0"></div>
              )}
            </div>
          ))}
        </div>

        {/* Timeline for Mobile */}
        <div className="mb-16 md:hidden">
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-600"></div>
            {steps.map((step, index) => (
              <div key={index} className="relative flex items-start mb-8">
                <div className="flex items-center justify-center flex-shrink-0 w-16 h-16 mr-6 text-xl font-bold text-white bg-blue-600 rounded-full">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-xl font-bold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mb-4 text-gray-600">
                    {step.description}
                  </p>
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="flex-shrink-0 w-4 h-4 mr-3 text-green-500" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}

      </div>
    </section>
  );
}; 